import React from 'react';
import { Typography } from 'antd';
import GearLoader from './GearLoader';

const { Text } = Typography;

// --- Framing the artwork ----------------------------------------------------
// GearLoader renders the approved SVG untouched, and that SVG carries wide
// empty margins by construction: on its 680x320 canvas the drawing itself
// only spans x 182..459, y 72..288 -- the middle 41% of the width. Dropped
// into a card as-is, most of the card is empty and the gears read as a small
// icon rather than as the wait state.
//
// So the framing happens out here instead of in the artwork: the SVG is blown
// up past the container's own width and the empty sides are allowed to spill
// out of an overflow:hidden box. Nothing about the drawing changes -- it is
// the same file, just framed closer, the way you would crop a photo without
// repainting it.
//
// Every number below is derived from the box above rather than eyeballed, so
// the art stays centred with equal headroom at any width.
const VIEWBOX_WIDTH = 680;
const VIEWBOX_HEIGHT = 320;
const ART_LEFT = 182;
const ART_RIGHT = 459;
const ART_TOP = 72;
const ART_BOTTOM = 288;
/** SVG width as a multiple of the container's -- how far in the crop zooms. */
const ZOOM = 1.5;
/** Headroom kept above and below the art, in container widths, so teeth never touch an edge. */
const BREATHING_ROOM = 0.05;

/** One SVG user unit, expressed as a fraction of the container's width. */
const UNIT = ZOOM / VIEWBOX_WIDTH;
const ART_CENTER_X = (ART_LEFT + ART_RIGHT) / 2;

const asPercent = (fraction: number) => `${(fraction * 100).toFixed(3)}%`;

// Percentage margins resolve against the container's WIDTH (both axes), which
// is what makes this scale cleanly: the negative top/bottom ones also pull the
// container's own height in to hug the art, so callers never set a height.
const CROP_STYLE: React.CSSProperties = {
  width: asPercent(ZOOM),
  marginLeft: asPercent(0.5 - ART_CENTER_X * UNIT),
  marginTop: asPercent(-(ART_TOP * UNIT - BREATHING_ROOM)),
  marginBottom: asPercent(-((VIEWBOX_HEIGHT - ART_BOTTOM) * UNIT - BREATHING_ROOM)),
  display: 'block',
};

export interface IGearLoadingStateProps {
  /**
   * Caller's own copy, already translated. Left out where the surrounding
   * chrome already says what is loading -- the Explore map's sheet header
   * does, and repeating it under the gears would say it twice.
   */
  message?: string;
}

/**
 * The app's wait state: the turning gears at a size that actually fills the
 * card, with optional copy underneath.
 *
 * This is the one place any screen should reach for while it waits on
 * results -- machinery, operators, accessories, whatever comes next -- so a
 * new list never has to decide what its loading state looks like.
 */
const GearLoadingState: React.FC<IGearLoadingStateProps> = ({ message }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      padding: '16px 0',
    }}
  >
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <div style={CROP_STYLE}>
        <GearLoader />
      </div>
    </div>
    {message && (
      <Text type="secondary" style={{ textAlign: 'center' }}>
        {message}
      </Text>
    )}
  </div>
);

export default GearLoadingState;
